'use client';
import React, { FC, MouseEventHandler, useEffect, useState } from 'react';
import {
  TypeImage,
  TypeListFields,
  TypeMediaTextTitle,
  TypeMediaTextTitleFields,
  TypePage,
  TypePlayKitPage,
  TypeVideoEmbedFields,
} from '@/core/generated-types';
import {
  CardsRow,
  HeaderText,
  LuvImage,
  YotpoReviewsWidget,
  TrustedShopReviews,
  UniversalCta,
  ValuePropComponentRow,
  RichText,
  Modal,
  DynamicComponent,
  VideoEmbed,
} from '@/components';
import { ShopifyProduct } from '@/lib/shopify';
import { HeaderSize } from '@/components/ui/HeaderText';
import { BackToTop } from '@/components/common/BackToTop';
import { Seo } from '@/components/ui/Seo';
import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';
import PlaykitSchema from '@/components/common/Schema/PlaykitSchema';
import {
  ExtendedProductFields,
  buildCanonicalUrl,
} from '@/core/contentfulConfig';
import ProductSchema from '@/components/common/Schema/ProductSchema';
import { Entry } from 'contentful';
import {
  shouldUseYotpoReviews,
  shouldUseTrustedShopsReviews,
  getLocaleInfo,
} from '@/utils/helpers';
import { ReviewTotals } from '@/lib/yotpo';
import ReviewsSchema from '@/components/common/Schema/ReviewsSchema';
import { PRODUCT_EVENT_TYPES } from '@/utils/rudderstack';
import { usePathname, useSearchParams } from 'next/navigation';
import { Document } from '@contentful/rich-text-types';
import { StartingKit, useStartingKit } from '@/hooks/useStartingKit';
import { parseDigiohDob } from '@/utils/dates/dateFormatters';
import useLocale from '@/hooks/useLocale';
import { useLocalStorage } from 'usehooks-ts';
import { setLocalStorage } from '@/utils/localStorage';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorage';
import {
  getTranslations,
  QR_DATA_KEY,
} from '@/components/subflow-v3/components/utils';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';
import { PlayKitDetailsHeader } from '@/components/subflow-v3/components/PlayKitDetailsHeader';
import { Divider } from '@/components/subflow-v3/components/Divider';
import Customizations from '@/components/subflow-v3/components/Customizations';

interface ListItemInterface {
  listItems: TypeMediaTextTitle[];
  headerText: string;
}
const ForTheParents = ({
  forTheParentsList,
}: {
  forTheParentsList: Entry<TypeListFields>;
}) => {
  return (
    <>
      {/* Workaround to not change parent's shared component style */}

      <div className="for-parents-streamlined bg-white mt-7 -mx-6 md:mx-0 lg:mt-16">
        <HeaderText
          classes="text-center mb-2 md:text-3xl lg:text-3xl"
          size={HeaderSize.H2}
          text={forTheParentsList?.fields?.title || 'For Parents'}
        />
        <div className="-mt-20 -pb-20 md:pb-0">
          <ValuePropComponentRow
            components={
              forTheParentsList.fields
                .listItems as Entry<TypeMediaTextTitleFields>[]
            }
            entryName={
              forTheParentsList.fields.entryName || 'parent list items'
            }
            display="carousel"
          />
        </div>
      </div>
    </>
  );
};

const LookInside = ({ listItems, headerText }: ListItemInterface) => {
  if (!listItems || !listItems.length) return null;
  return (
    <>
      <div
        className="mx-auto max-w-[1200px] px-6 pt-6 md:px-0"
        id="what-is-inside"
      >
        <HeaderText
          classes="mb-5 text-center lg:text-3xl lg:mb-9"
          size={HeaderSize.H2}
          text={headerText}
        />
        <div className="lg:grid lg:grid-cols-3 lg:gap-x-6 lg:gap-y-12">
          {listItems.map((item, i) => (
            <div
              key={`${item.sys.id}-${i}`}
              className="mb-6 flex flex-col items-center text-center w-full items-start gap-1 lg:mb-0"
            >
              <div className="flex-none">
                <LuvImage
                  imageData={item?.fields?.image}
                  width={328}
                  height={328}
                />
              </div>
              <div className="flex-auto">
                <HeaderText
                  size={HeaderSize.H3}
                  text={item?.fields?.textHeading}
                />
                {item?.fields?.textBody && (
                  <div className="mt-1">
                    <RichText content={item.fields.textBody as Document} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export interface PlayKitPageLayoutProps extends TypePlayKitPage {
  shopifyProduct: ShopifyProduct | null;
  yotpoReviews?: ReviewTotals;
  theThinkerPlayKit: StartingKit;
  v3Translations: TypePage;
}

export const PlayKitPageLayout: FC<PlayKitPageLayoutProps> = ({
  shopifyProduct,
  yotpoReviews,
  v3Translations,
  theThinkerPlayKit,
  ...page
}) => {
  const pathname = usePathname();
  const t = getTranslations(v3Translations);
  const locale = useLocale();
  const localeInfo = getLocaleInfo(locale);
  const [language] = useLocalStorage(
    'subflow-v3-language',
    localeInfo.language,
  );

  useEffect(() => {
    if (!shopifyProduct) return;
    const [variant] = shopifyProduct.variants;
    const qtyVal = 1;
    pushEvent('productView', { qtyVal, shopifyProduct, variant });
    captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
      shopifyProduct,
      variant,
      product: page.fields.product.fields,
      quantity: qtyVal,
      url: pathname || '',
    });
  }, [page.fields.product.fields, pathname, shopifyProduct]);

  const {
    fields: {
      product,
      lookInsideList,
      forTheParentsList,
      marketingComponents,
      relatedProducts,
      enableYotpoReviewsWidget,
      enableTrustedShopsReviewsWidget,
      aboutHeading,
      slug,
    },
  } = page;

  setLocalStorage(LOCAL_STORAGE_KEYS.playKitSeen, {
    title: product.fields.title,
    image: `https:${product.fields.listingImage.fields.Image.fields.file.url}`,
    link: pathname,
  });

  const metadataFields = page.fields?.pageMetafields?.fields;
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] =
    useState(false);

  const jessVideo = marketingComponents?.find(
    ({ fields }) => fields.componentName === 'VideoEmbed',
  ) as Entry<TypeVideoEmbedFields>;

  const warning = marketingComponents?.find(
    ({ fields }) => fields.componentName === 'Disclaimer',
  );
  const newMarketingComponents = marketingComponents?.filter(
    (item) => !item.fields.componentName,
  );

  const hasCustomizations =
    !!theThinkerPlayKit?.customizable_options.length || false;

  const onCTAClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (hasCustomizations) {
      e.preventDefault();
      setIsCustomizationModalOpen(true);
    }
  };

  const searchParams = useSearchParams();
  const qrTargetProduct = searchParams?.get('qrTargetProduct');

  const digiohDobValue =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('digioh_dob')
      : null;
  const formattedDigiohDob = parseDigiohDob(digiohDobValue || '');

  const { data: startingKit, isLoading: isStartingKitLoading } = useStartingKit(
    formattedDigiohDob,
    language,
  );

  const { api } = useSubFlowV3Data();

  useEffect(() => {
    if (isStartingKitLoading || !digiohDobValue || !api) return;

    api.updateChildInfo({
      month: formattedDigiohDob.month,
      day: formattedDigiohDob.day,
      year: formattedDigiohDob.year,
    });

    const ageRange = product.fields.ageRange || product.fields.ageDescription;

    if (product.fields.cta && ageRange && startingKit?.age_band === ageRange) {
      window.localStorage.setItem('subflow-v3-skipped-child-info', 'true');
      product.fields.cta.fields.linkUrl =
        '/pages/subscription-v3/payment-options';
    }
  }, [isStartingKitLoading, digiohDobValue, startingKit]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!window || !qrTargetProduct || qrTargetProduct !== 'kit-the-charmer')
        return window.localStorage.setItem(
          QR_DATA_KEY,
          JSON.stringify({ lookerInTargetQR: false }),
        );
      window.localStorage.setItem(
        QR_DATA_KEY,
        JSON.stringify({ lookerInTargetQR: true }),
      );
    }
  }, [qrTargetProduct]);

  return (
    <div className="playkit-pdp playkit-pdp-streamlined">
      <PlaykitSchema product={product} slug={slug} />
      {shopifyProduct && (
        <div itemScope itemType="https://schema.org/Product">
          <ProductSchema product={product as Entry<ExtendedProductFields>} />
          {shouldUseYotpoReviews() && enableYotpoReviewsWidget && (
            <ReviewsSchema
              totals={yotpoReviews}
              product={product as Entry<ExtendedProductFields>}
            />
          )}
        </div>
      )}
      <Seo
        title={metadataFields?.pageTitle}
        description={metadataFields?.pageDescription}
        nofollow={metadataFields?.noFollow}
        noindex={metadataFields?.noIndex}
        canonical={buildCanonicalUrl(page)}
      />
      <div className="mx-auto max-w-[1200px] px-6 pt-6 md:px-10">
        <PlayKitDetailsHeader
          product={product}
          carousel={[
            ...(jessVideo ? [jessVideo] : []),
            ...(lookInsideList?.fields?.listItems?.map(
              ({ fields }) => fields?.image,
            ) as TypeImage[]),
          ]}
          aboutHeading={aboutHeading || ''}
          noCommitmentMessage={t.noCommitmentMessage}
          featuresHeading={t.keyFeatures}
          onCTAClick={onCTAClick}
        />
      </div>
      {lookInsideList?.fields?.listItems && (
        <>
          <Divider className="mt-8 relative lg:mt-20" />
          <div className="bg-aqua-tint py-10 px-6 -mt-5">
            <LookInside
              listItems={
                lookInsideList?.fields?.listItems as TypeMediaTextTitle[]
              }
              headerText={t.insideThePlayKit}
            />
            <div className="text-center mx-6 lg:mt-16">
              {product.fields.cta && (
                <UniversalCta
                  link={product.fields.cta}
                  className="mt-4 mx-0 h-14 lg:h-16 lg:max-w-[360px] lg:mx-auto"
                  onClick={onCTAClick}
                />
              )}
              <p
                className="pb-2 mt-3 text-base md:text-lg lg:mt-4"
                dangerouslySetInnerHTML={{ __html: t.noCommitmentMessage }}
              />
            </div>
          </div>
        </>
      )}
      {jessVideo && (
        <>
          <VideoEmbed
            platform={jessVideo.fields.platform}
            componentName={jessVideo.fields.componentName}
            videoId={jessVideo.fields.videoId}
            videoParams={jessVideo.fields.videoParams}
            classes="w-full h-[270px] md:h-[500px] md:my-20"
          />
        </>
      )}
      <div className="mx-auto">
        {marketingComponents &&
          newMarketingComponents?.map((component, i) => (
            <DynamicComponent key={component.sys.id} component={component} />
          ))}
      </div>

      <div className="mx-auto max-w-[1200px] px-6 pt-6 md:px-10">
        <ForTheParents forTheParentsList={forTheParentsList} />
        {shouldUseYotpoReviews() && enableYotpoReviewsWidget && (
          <YotpoReviewsWidget product={product} />
        )}
        <DynamicComponent component={warning} />
        {shouldUseTrustedShopsReviews() && enableTrustedShopsReviewsWidget && (
          <div className="container mx-auto mb-24 px-6">
            <TrustedShopReviews
              shopifyProduct={shopifyProduct}
              widgetType="reviews"
            />
          </div>
        )}
      </div>

      {relatedProducts && (
        <>
          <div className="mx-auto my-12">
            <CardsRow {...relatedProducts.fields} />
          </div>
        </>
      )}

      <BackToTop />
      {hasCustomizations && isCustomizationModalOpen && (
        <Modal
          isOpen={hasCustomizations && isCustomizationModalOpen}
          handleClose={() => setIsCustomizationModalOpen(false)}
          contentClasses="flex justify-center flex-col items-center pb-6 -mb-4 px-1 md:px-6"
        >
          <Customizations t={t} kit={theThinkerPlayKit} />
          {product.fields.secondaryCta && (
            <div className="mx-6 w-full flex justify-center">
              <UniversalCta
                link={product.fields.secondaryCta}
                className="mt-5 h-14 w-full lg:h-16 lg:max-w-[360px] md:mt-8"
              />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};
